import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Form } from '@unform/web';
import { toast } from 'react-toastify';
import { FormHandles } from '@unform/core';
import * as Yup from 'yup';
import { FiArrowLeft } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';

import api from 'services/api';
import { DiasDaSemana } from 'utils/variables';
import { Blocos } from 'utils/eleicoes';
import AppError from 'utils/AppError';
import getValidationErrors from 'services/getValidationErros';

import Button from 'components/Button';
import Input from 'components/Input';
import Accordion, { AccordionItem } from 'components/Accordion';
import Radio from 'components/Radio';
import Select from 'components/Select';
import Spinner from 'components/Spinner';

import { BlocoHeader, ColumnContent, Container, IpPtColumn } from './styles';

interface Blocos {
  ipptinicio: string;
  numipptinicio: number;
  programainicio: { id: number; nome: string };
  ipptfim: string;
  numipptfim: number;
  programafim: { id: number; nome: string };
}
interface IntervaloEleicao {
  dia: string;
  blocos: Blocos[];
}

// configuração do validator
const schema = Yup.object().shape({
  programainicio_1: Yup.string().required('Campo Obrigatório'),
  programafim_1: Yup.string().required('Campo Obrigatório'),
  ipptinicio_1: Yup.string().required('Campo Obrigatório'),
  ipptfim_1: Yup.string().required('Campo Obrigatório'),
  numipptinicio_1: Yup.number()
    .required('Campo Obrigatório')
    .typeError('Num IP/PT deve ser um número')
    .max(10, 'Valor inválido')
    .min(1, 'Valor inválido'),
  numipptfim_1: Yup.number()
    .required('Campo Obrigatório')
    .typeError('Num IP/PT deve ser um número')
    .max(10, 'Valor inválido')
    .min(1, 'Valor inválido'),
  programainicio_2: Yup.string().required('Campo Obrigatório'),
  programafim_2: Yup.string().required('Campo Obrigatório'),
  ipptinicio_2: Yup.string().required('Campo Obrigatório'),
  ipptfim_2: Yup.string().required('Campo Obrigatório'),
  numipptinicio_2: Yup.number()
    .required('Campo Obrigatório')
    .typeError('Num IP/PT deve ser um número')
    .max(10, 'Valor inválido')
    .min(1, 'Valor inválido'),
  numipptfim_2: Yup.number()
    .required('Campo Obrigatório')
    .typeError('Num IP/PT deve ser um número')
    .max(10, 'Valor inválido')
    .min(1, 'Valor inválido'),
  programainicio_3: Yup.string().required('Campo Obrigatório'),
  programafim_3: Yup.string().required('Campo Obrigatório'),
  ipptinicio_3: Yup.string().required('Campo Obrigatório'),
  ipptfim_3: Yup.string().required('Campo Obrigatório'),
  numipptinicio_3: Yup.number()
    .required('Campo Obrigatório')
    .typeError('Num IP/PT deve ser um número')
    .max(10, 'Valor inválido')
    .min(1, 'Valor inválido'),
  numipptfim_3: Yup.number()
    .required('Campo Obrigatório')
    .typeError('Num IP/PT deve ser um número')
    .max(10, 'Valor inválido')
    .min(1, 'Valor inválido'),
});

export default function IntervaloEleicao() {
  // react-router-dom
  const navigate = useNavigate();

  const formRef = useRef<FormHandles>(null);

  /**
   * USE STATE
   */
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [loading, setLoading] = useState(true);
  const [intervaloEleicao, setIntervaloEleicao] = useState<IntervaloEleicao[]>(
    [],
  );
  const [programas, setProgramas] = useState([]);
  const [initialData, setInitialData] = useState({});
  const [activeDay, setActiveDay] = useState(0);

  /**
   * USE CALLBACK
   */

  const normalizeData = useCallback(
    data => {
      return {
        dia: DiasDaSemana.find(d => d.id === activeDay)?.eng || '',
        blocos: Blocos.map(b => ({
          bloco: b.value,
          programainicio: data[`programainicio_${b.value}`],
          programafim: data[`programafim_${b.value}`],
          ipptinicio: data[`ipptinicio_${b.value}`],
          ipptfim: data[`ipptfim_${b.value}`],
          numipptinicio: data[`numipptinicio_${b.value}`],
          numipptfim: data[`numipptfim_${b.value}`],
        })),
      };
    },
    [activeDay],
  );

  const handleSubmit = useCallback(
    async data => {
      setLoadingSubmit(true);
      try {
        if (formRef.current) formRef.current.setErrors({});
        await schema.validate({ ...data }, { abortEarly: false });

        const normalizedData = normalizeData(data);
        const response = await api.put('/intervaloeleicao', normalizedData);
        if (response && response.data) {
          toast.success(
            `Informações de ${
              DiasDaSemana.find(d => d.id === activeDay)?.pt
            } salvas com sucesso`,
          );
          console.log(response.data);
          setIntervaloEleicao(response.data);
        } else
          toast.error(
            `Falha ao salvar informações de ${
              DiasDaSemana.find(d => d.id === activeDay)?.pt
            }`,
          );
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);
          if (formRef.current) formRef.current.setErrors(errors);
          toast.error(err.message);
        } else if (err instanceof Error) {
          toast.error(err.message);
        } else {
          const appError = err as AppError;
          toast.error(appError.message);
        }
      } finally {
        setLoadingSubmit(false);
      }
    },
    [activeDay, normalizeData],
  );
  const renderForm = useCallback(
    d => {
      if (d.id === activeDay) {
        return (
          <Form
            initialData={initialData}
            ref={formRef}
            onSubmit={handleSubmit}
            autoComplete="off"
          >
            <div>
              <ColumnContent $columns="1fr 1fr 1fr">
                {Blocos.map(b => (
                  <div key={b.value}>
                    <BlocoHeader $background={b.value}>{b.label}</BlocoHeader>
                    <div>
                      <section>
                        <h5>Início</h5>
                      </section>
                      <Select
                        type="default"
                        errorField=""
                        name={`programainicio_${b.value}`}
                        placeholder="Selecione um programa ..."
                        options={programas}
                        isSearchable
                        isClearable
                        disabled={loadingSubmit}
                        fill={false}
                      />
                      <IpPtColumn>
                        <Radio
                          name={`ipptinicio_${b.value}`}
                          options={[
                            { id: 'ip', label: 'IP' },
                            { id: 'pt', label: 'PT' },
                          ]}
                        />

                        <Input
                          name={`numipptinicio_${b.value}`}
                          type="number"
                          placeholder="Num IP/PT *"
                        />
                      </IpPtColumn>
                      <section>
                        <h5>Fim: </h5>
                      </section>
                      <Select
                        type="default"
                        errorField=""
                        name={`programafim_${b.value}`}
                        placeholder="Selecione um programa ..."
                        options={programas}
                        isSearchable
                        isClearable
                        disabled={loadingSubmit}
                        fill={false}
                      />
                      <IpPtColumn>
                        <Radio
                          name={`ipptfim_${b.value}`}
                          options={[
                            { id: 'ip', label: 'IP' },
                            { id: 'pt', label: 'PT' },
                          ]}
                        />
                        <Input
                          name={`numipptfim_${b.value}`}
                          type="number"
                          placeholder="Num IP/PT *"
                        />
                      </IpPtColumn>
                    </div>
                  </div>
                ))}
              </ColumnContent>
              <div className="ButtonGroup">
                <Button type="submit" disabled={loadingSubmit} color="primary">
                  {!loadingSubmit ? 'SALVAR' : <Spinner type="ThreeDots" />}
                </Button>
              </div>
            </div>
          </Form>
        );
      } else {
        return <></>;
      }
    },
    [activeDay, handleSubmit, initialData, loadingSubmit, programas],
  );

  const handleAccordionChange = useCallback(
    (uuids: string[]) => {
      if (uuids[0]) {
        setActiveDay(Number(uuids[0]));
        try {
          const bloco1 = intervaloEleicao[Number(uuids[0]) - 1].blocos[0];
          const bloco2 = intervaloEleicao[Number(uuids[0]) - 1].blocos[1];
          const bloco3 = intervaloEleicao[Number(uuids[0]) - 1].blocos[2];
          setInitialData({
            programainicio_1: bloco1.programainicio,
            ipptinicio_1: bloco1.ipptinicio,
            numipptinicio_1: bloco1.numipptinicio,
            programafim_1: bloco1.programafim,
            ipptfim_1: bloco1.ipptfim,
            numipptfim_1: bloco1.numipptfim,

            programainicio_2: bloco2.programainicio,
            ipptinicio_2: bloco2.ipptinicio,
            numipptinicio_2: bloco2.numipptinicio,
            programafim_2: bloco2.programafim,
            ipptfim_2: bloco2.ipptfim,
            numipptfim_2: bloco2.numipptfim,

            programainicio_3: bloco3.programainicio,
            ipptinicio_3: bloco3.ipptinicio,
            numipptinicio_3: bloco3.numipptinicio,
            programafim_3: bloco3.programafim,
            ipptfim_3: bloco3.ipptfim,
            numipptfim_3: bloco3.numipptfim,
          });
        } catch (error) {
          console.log(error);
          toast.error('Intervalos não cadastrados, comunique o administrador');
        }
      }
    },
    [intervaloEleicao],
  );

  useEffect(() => {
    async function loadIntervalos() {
      setLoading(true);
      await api
        .get('/intervaloeleicao')
        .then(response => {
          if (response && response.data) {
            setIntervaloEleicao(response.data);
          }
        })
        .catch(error => {
          if (error instanceof Error) {
            toast.error(error.message);
          } else console.log(error);
        })
        .finally(() => {
          setLoading(false);
        });
    }
    loadIntervalos();
  }, []);

  useEffect(() => {
    async function loadProgramas() {
      try {
        const response = await api.get('/programas');
        if (response && response.data)
          setProgramas(
            response.data.map(
              (prog: {
                id: number;
                sigla: string;
                mnemonico: string;
                nome: string;
              }) => {
                return {
                  value: prog.id,
                  label:
                    (prog.sigla ? prog.sigla + ' - ' : '') +
                    (prog.mnemonico ? prog.mnemonico : '') +
                    (prog.nome ? ' (' + prog.nome + ')' : ''),
                };
              },
            ),
          );
        else toast.error('Falha de comunicação com o sistema');
      } catch (error) {
        if (error instanceof Error) {
          toast.error(error.message);
        } else {
          const appError = error as AppError;
          toast.error(appError.message);
        }
      }
    }
    loadProgramas();
  }, []);

  return (
    <Container>
      <header>
        <Button color="primary" onClick={() => navigate(-1)}>
          VOLTAR
          <FiArrowLeft />
        </Button>
      </header>
      {!loading && (
        <Accordion onChange={handleAccordionChange}>
          {DiasDaSemana.map(d => (
            <AccordionItem
              key={`item_${d.id}`}
              uuid={d.id.toString()}
              title={d.pt}
            >
              {renderForm(d)}
            </AccordionItem>
          ))}
        </Accordion>
      )}
    </Container>
  );
}
